import React, { useState, useEffect, useRef } from 'react';
import { useGeneral } from '../../../contexts';
import { HiChevronDown } from 'react-icons/hi';
import styles from './PageSelector.module.css';

export interface PageSelectorItem {
  key: string;
  translationKey: string;
  path: string;
}

interface PageSelectorProps {
  items: PageSelectorItem[];
  activeKey: string;
  onNavigate: (path: string) => void;
  basePath: string;
}

const PageSelector: React.FC<PageSelectorProps> = ({
  items,
  activeKey,
  onNavigate,
  basePath
}) => {
  const { language } = useGeneral();
  const { getTranslation } = language;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleItemClick = (item: PageSelectorItem) => {
    const fullPath = `${basePath}${item.path}`;
    onNavigate(fullPath);
    setIsDropdownOpen(false);
  };

  const activeItem = items.find(item => item.key === activeKey) || items[0];

  return (
    <nav className={styles.pageSelector} ref={dropdownRef}>
      {/* Mobile dropdown button */}
      <button
        className={styles.pageSelectorDropdownButton}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <div className={styles.pageSelectorDropdownContent}>
          <span className={styles.pageSelectorDropdownText}>
            {getTranslation(activeItem.translationKey) || activeItem.key}
          </span>
        </div>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
        {/* @ts-ignore - react-icons compatibility with React 19 */}
        <HiChevronDown 
          size={20} 
          className={`${styles.pageSelectorChevron} ${isDropdownOpen ? styles.pageSelectorChevronOpen : ''}`}
        />
      </button>

      {/* Mobile dropdown menu */}
      {isDropdownOpen && (
        <div className={styles.pageSelectorDropdown}>
          {items
            .filter((item) => item.key !== activeKey)
            .map((item) => (
              <button
                key={item.key}
                className={styles.pageSelectorDropdownItem}
                onClick={() => handleItemClick(item)}
              >
                {getTranslation(item.translationKey) || item.key}
              </button>
            ))}
        </div>
      )}

      {/* Desktop list view */}
      <div className={styles.pageSelectorDesktop}>
        {items.map((item) => (
          <button
            key={item.key}
            className={`${styles.pageSelectorItem} ${activeKey === item.key ? styles.pageSelectorItemActive : ''}`}
            onClick={() => handleItemClick(item)}
          >
            {getTranslation(item.translationKey) || item.key}
          </button>
        ))}
      </div>
    </nav>
  );
};

export default PageSelector;

